import { useEffect, useRef, useState } from 'react';
import { getEffectiveTheme, subscribeTheme } from '../lib/theme';

/**
 * Cloudflare Turnstile（ログインフォームの CAPTCHA。割賦販売法セキュリティ申告書 1-③ 対応）。
 * VITE_TURNSTILE_SITE_KEY が未設定の環境（段階導入前・ローカル開発）では何もレンダリングせず、
 * スクリプトの読み込みすら行わない（＝フォールバックとして captchaToken 無しで従来通りログインできる）。
 */
const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

interface TurnstileRenderOptions {
  sitekey: string;
  theme?: 'light' | 'dark' | 'auto';
  callback: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
}

interface TurnstileGlobal {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileGlobal;
  }
}

let scriptPromise: Promise<void> | null = null;

/** Turnstile の JS をページ全体で一度だけ動的に読み込む。 */
function loadTurnstileScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${SCRIPT_SRC}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('CAPTCHA の読み込みに失敗しました。'));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

interface TurnstileProps {
  /** トークン取得時（および期限切れ・エラー時は null）に呼ばれる。 */
  onVerify: (token: string | null) => void;
}

/** サイトキー未設定なら null を返す（＝ウィジェットを出さない）。 */
export default function Turnstile({ onVerify }: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [error, setError] = useState('');
  // ウィジェットがページのダーク/ライトと馴染むよう、実効テーマが変わるたびに描画し直す。
  const [theme, setTheme] = useState(() => getEffectiveTheme());
  useEffect(() => subscribeTheme(setTheme), []);

  useEffect(() => {
    if (!SITE_KEY) return;
    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          theme,
          callback: (token) => onVerify(token),
          'expired-callback': () => onVerify(null),
          'error-callback': () => onVerify(null),
        });
      })
      .catch((e) => {
        if (!cancelled) setError((e as Error).message);
      });
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  if (!SITE_KEY) return null;

  return (
    <div className="turnstile-field">
      <div ref={containerRef} />
      {error && <div className="auth-form__error">{error}</div>}
    </div>
  );
}
